import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { globalProjectNameSearch } from "../../api/customer/masters/adminMastersApi";
import { toast } from "react-toastify";
import { LucidLoader } from "../Icons";
import { globalProjectNameSearchSupplier } from "../../api/supplier/rfqs/adminRfqApi";

const uniqueFieldForProjectNameBasedOnType = {
    quote: "version",
    order: "orderId",
    invoice: "invoiceNumber",
    rfq: "quotationRefId",
    offer: "quotationRefId",
    purchaseOrder: "poNumber",
    bill: "billNumber",
};

function GlobalSearchModal() {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const debounceTimeout = useRef(null);
    const [preNavigatedEntity, setPreNavigatedEntity] = useState(null);
    const loaderRef = useRef(null);
    const navigate = useNavigate();
    let token = localStorage.getItem("Token");

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.ctrlKey && e.key === "k") {
                e.preventDefault();
                setOpen((prev) => !prev);
            }
            if (e.key === "Escape") {
                setOpen(false);
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [navigate]);

    const fetchResults = useCallback(
        async (value, pageNumber = 1) => {
            try {
                if (!value || value.length < 0) {
                    setResults([]);
                    setHasMore(false);
                    return;
                }

                setLoading(true);
                const [dataFromMake, dataFromSupplier] = await Promise.all([
                    globalProjectNameSearch(value, pageNumber),
                    globalProjectNameSearchSupplier(token, value, pageNumber),
                ]);

                let newResult = [];
                if (dataFromMake?.success && Array.isArray(dataFromMake.data)) {
                    newResult = [...dataFromMake.data];
                }
                if (dataFromSupplier?.success && Array.isArray(dataFromSupplier.data)) {
                    newResult = [...newResult, ...dataFromSupplier.data];
                }

                setResults((prev) =>
                    pageNumber === 1 ? newResult : [...prev, ...newResult]
                );

                setHasMore(newResult.length > 0);
            } catch (error) {
                console.error("Error ::", error);
                toast.error("Failed to search project name");
            } finally {
                setLoading(false);
            }
        },
        [token]
    );

    const handleSearch = (value) => {
        setQuery(value);
        setPage(1);

        if (debounceTimeout.current) clearTimeout(debounceTimeout.current);

        debounceTimeout.current = setTimeout(() => {
            if (value.length >= 1) {
                fetchResults(value, 1);
            } else {
                setResults([]);
            }
        }, 1000);
    };

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                const target = entries[0];
                if (target.isIntersecting && hasMore && !loading) {
                    setPage((prev) => prev + 1);
                }
            },
            { threshold: 1.0 }
        );
        if (loaderRef.current) observer.observe(loaderRef.current);
        return () => observer.disconnect();
    }, [hasMore, loading]);

    useEffect(() => {
        if (page > 1 && query.length >= 1) {
            fetchResults(query, page);
        }
    }, [page, query, fetchResults]);

    const handleOverlayClick = (e) => {
        if (e.target.id === "overlay") setOpen(false);
    };

    if (!open) return null;

    const showProjectName = (item) => {
        return `${item?.projectName} (${item[uniqueFieldForProjectNameBasedOnType[item.type]]})`;
    };

    const handleClick = (item) => {
        const { _id, type } = item;
        setOpen(false);

        let path = "";

        switch (type) {
            case "quote":
                path = `/admin/admin-quote/${_id}`;
                break;
            case "order":
                path = `/admin/orders/${_id}`;
                break;
            case "invoice":
                path = `/admin/invoice-details/${_id}`;
                break;
            case "rfq":
                path = `/admin/rfq-details/${_id}`;
                break;
            case "offer":
                path = `/admin/offers-details/${_id}`;
                break;
            case "purchaseOrder":
                path = `/admin/purchase-order-details/${_id}`;
                break;
            case "bill":
                path = `/admin/bill-details/${_id}`;
                break;
            default:
                console.error("Navigation error");
                return;
        }

        navigate(path);
        if (preNavigatedEntity == type) {
            setTimeout(() => window.location.reload(), 100);
        }
        setPreNavigatedEntity(type);
    };


    return (
        <div
            id="overlay"
            onClick={handleOverlayClick}
            style={{
                zIndex: 1000,
            }}
            className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-start justify-center "
        >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl p-4 mt-3 transition-all">
                <input
                    type="text"
                    placeholder="Search projects..."
                    value={query}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="border border-gray-300 w-full p-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                    autoFocus
                />

                <ul className="mt-2 border rounded-xl bg-white max-h-96 vh overflow-y-auto">
                    {loading && results.length === 0 && (
                        <li className="p-3 text-gray-500 text-center flex items-center justify-center">
                            <LucidLoader additionalClass={"text-primaryColor"} />
                        </li>
                    )}

                    {!loading && results.length === 0 && (
                        <li className="p-3 text-gray-500 text-center">
                            No results found
                        </li>
                    )}

                    {results.map((item, idx) => (
                        <li
                            key={idx}
                            onClick={() => handleClick(item)}
                            className="p-3 cursor-pointer  hover:text-white hover:bg-primaryColor"
                        >
                            {showProjectName(item)}{" "}
                            <span className="text-sm hover:text-white hover:bg-primaryColor">({item.type})</span>
                        </li>
                    ))}

                    {loading && results.length > 0 && (
                        <li className="p-3 text-gray-500 flex items-center justify-center">
                            <LucidLoader additionalClass={"text-primaryColor"} />
                        </li>
                    )}

                    <div ref={loaderRef} />
                </ul>
            </div>
        </div>
    );
}

export default GlobalSearchModal;
